import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { changelog, currentVersion, type ChangeKind } from '@/constants/changelog';

const KINDS: { kind: ChangeKind; label: string; className: string }[] = [
  { kind: 'new', label: 'New', className: 'text-success' },
  { kind: 'improved', label: 'Improved', className: 'text-info' },
  { kind: 'fixed', label: 'Fixed', className: 'text-muted-foreground' },
];

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

interface WhatsNewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const WhatsNewDialog: React.FC<WhatsNewDialogProps> = ({ open, onOpenChange }) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>What&apos;s new</DialogTitle>
          {currentVersion && <DialogDescription>You&apos;re on {currentVersion}</DialogDescription>}
        </DialogHeader>
        <div className="max-h-[60vh] space-y-6 overflow-y-auto pr-1">
          {changelog.map((release, index) => (
            <section
              key={release.version}
              className={index > 0 ? 'border-t border-border pt-6' : undefined}
            >
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="text-sm font-semibold text-foreground">{release.title}</h3>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge variant="secondary" className="h-5 px-2 py-0 font-medium tabular-nums">
                    {release.version}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{formatDate(release.date)}</span>
                </div>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{release.summary}</p>

              <div className="mt-4 space-y-3">
                {KINDS.map(({ kind, label, className }) => {
                  const items = release.changes.filter((c) => c.kind === kind);
                  if (items.length === 0) return null;
                  return (
                    <div key={kind}>
                      <div
                        className={`text-[11px] font-semibold uppercase tracking-wide ${className}`}
                      >
                        {label}
                      </div>
                      <ul className="mt-1.5 space-y-1.5">
                        {items.map((change, i) => (
                          <li key={i} className="flex gap-2.5 text-sm text-foreground/90">
                            <span className="mt-[7px] size-1 shrink-0 rounded-full bg-muted-foreground/50" />
                            <span>{change.text}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsNewDialog;
